import { useForm, ControllerRenderProps } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@predoc/cow';
import { DatePickerInput } from '@/components/DatePickerInput';
import { triggerDownload } from '@/utils/downloadUtils';
import { useExportPatientLogs } from '../../hooks/patientsAPI';

const exportAuditTrailSchema = z
  .object({
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      const start = new Date(data.startDate);
      const end = new Date(data.endDate);
      return start <= end;
    },
    {
      message: 'End date must be after or equal to start date.',
      path: ['endDate'],
    },
  );

type ExportAuditTrailFormData = z.infer<typeof exportAuditTrailSchema>;

type ExportAuditTrailFormFieldProps<T extends keyof ExportAuditTrailFormData> =
  {
    field: ControllerRenderProps<ExportAuditTrailFormData, T>;
  };

interface ExportAuditTrailDialogProps {
  patientName: string;
  patientId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ExportAuditTrailDialog = ({
  patientName,
  patientId,
  isOpen,
  onClose,
}: ExportAuditTrailDialogProps) => {
  const exportLogs = useExportPatientLogs();

  const form = useForm<ExportAuditTrailFormData>({
    resolver: zodResolver(exportAuditTrailSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleExport = async (values: ExportAuditTrailFormData) => {
    try {
      const data = await exportLogs.mutateAsync({
        patientId,
        startDate: values.startDate,
        endDate: values.endDate,
      });
      triggerDownload(data.signedUrl);
      handleClose();
    } catch (err) {
      // User-facing toast handled in useExportPatientLogs onError
      console.error('Export audit logs failed', err);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open: boolean) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{patientName}: Export Audit Trail</DialogTitle>
          <DialogDescription>
            Please select the event date range to export.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleExport)}>
            <div className="py-4">
              <FormLabel className="mb-2 block text-sm font-medium">
                Event Date Range
              </FormLabel>
              <div className="flex items-start gap-2">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({
                    field,
                  }: ExportAuditTrailFormFieldProps<'startDate'>) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <DatePickerInput
                          onChange={(date: Date | undefined) => {
                            field.onChange(
                              date ? format(date, 'yyyy-MM-dd') : '',
                            );
                          }}
                          value={
                            field.value ? parseISO(field.value) : undefined
                          }
                          placeholder="Start Date"
                          disableDates="future"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <span className="text-muted-foreground mt-2">–</span>
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({
                    field,
                  }: ExportAuditTrailFormFieldProps<'endDate'>) => (
                    <FormItem className="flex-1">
                      <FormControl>
                        <DatePickerInput
                          onChange={(date: Date | undefined) => {
                            field.onChange(
                              date ? format(date, 'yyyy-MM-dd') : '',
                            );
                          }}
                          value={
                            field.value ? parseISO(field.value) : undefined
                          }
                          placeholder="End Date"
                          disableDates="future"
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="submit"
                disabled={exportLogs.isPending}
                loading={exportLogs.isPending}
              >
                {exportLogs.isPending ? 'Exporting…' : 'Export'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
